import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { createQuestion, fetchLabels, createLabel } from "../pages/services/questionService";
import {
  fetchAllSubjects,
  fetchModulesBySubject,
  fetchTopicsByModule,
  fetchSectionsByTopic,
} from "../pages/services/noteHierarchyService";
import HierarchyDropdown from "./formComponents/HierarchyDropdown";
import MultiSelectDropdown from "./formComponents/MultiSelectDropdown";
import AlternateAnswersField from "./formComponents/AlternateAnswersField";
import TestCasesField, { cleanTestCases } from "./formComponents/TestCasesField";
import StarterCodeField from "./formComponents/StarterCodeField";
import OptionsTable from "./OptionsTable";
import RichTextEditor from "./ReusableComponent/RichTextEditor";
import ImageUploadField from "./ReusableComponent/ImageUploadField";
import { isHtmlEmpty } from "./utils/utility";
import "../styles/AddForm.css";
import "../styles/AddQuestions.css";
import "../styles/QuestionForm.css";

const CODE_QUESTION_TYPE = "Code to compile with output";
const DIFFICULTY = ["Easy", "Medium", "Hard", "Very Hard", "Professional"];
const QUESTION_TYPES = [
  "Text",
  "Multiple Choice single selection",
  "Multiple Choice multi selection",
  "Descriptive",
  "Code to compile with output",
];

const Field = ({ label, required, error, children }) => (
  <div className="af-field">
    <label className="af-label">
      {label}
      {required && <span className="af-required">*</span>}
    </label>
    {children}
    {error && <span className="af-error">{error}</span>}
  </div>
);

const errorBorder = { border: "1.5px solid #ef4444", borderRadius: "8px" };

const AddQuestions = () => {
  const [formData, setFormData] = useState({
    subject: "", module: "", section: "", topic: "",
    difficultyLevel: "", questionType: "",
    question: "", questionAttachment: "",
    answer: "", answerAttachment: "", answerExplanation: "",
    notes: "", questionLink: "", markPositive: "", markNegative: "",
    labels: [],
    alternateAnswers: [],
    testCases: [],
    starterCode: "",
  });

  const [subjects, setSubjects] = useState([]);
  const [modules, setModules] = useState([]);
  const [topics, setTopics] = useState([]);
  const [sections, setSections] = useState([]);
  const [availableLabels, setAvailableLabels] = useState([]);
  const [loadingModules, setLoadingModules] = useState(false);
  const [loadingTopics, setLoadingTopics] = useState(false);
  const [loadingSections, setLoadingSections] = useState(false);

  const [errors, setErrors] = useState({});
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [showOptionDialog, setShowOptionDialog] = useState(false);
  const [dupError, setDupError] = useState("");
  const [dupChecking, setDupChecking] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();

  const isMCQ =
    formData.questionType === "Multiple Choice single selection" ||
    formData.questionType === "Multiple Choice multi selection";

  useEffect(() => {
    fetchAllSubjects()
      .then(setSubjects)
      .catch(() => setSubjects([]));
    fetchLabels()
      .then(setAvailableLabels)
      .catch(() => setAvailableLabels([]));
  }, []);

  const handleCreateLabel = async (name) => {
    const created = await createLabel(name);
    setAvailableLabels((prev) => (prev.some((l) => l.value === created.value) ? prev : [...prev, created]));
    return created;
  };

  useEffect(() => {
    localStorage.removeItem("tempOptions");
    return () => localStorage.removeItem("tempOptions");
  }, [formData.questionType]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
    clearError(name);
  };

  const clearError = (name) => {
    setErrors((p) => {
      const n = { ...p };
      delete n[name];
      if (Object.keys(n).length === 0) setErrorMessage("");
      return n;
    });
  };

  const checkDuplicate = async (html) => {
    const text = (html || "")
      .replace(/<[^>]*>/g, " ")
      .replace(/&[a-zA-Z0-9#]+;/g, " ")
      .replace(/\s+/g, " ")
      .trim();
    if (!text) { setDupError(""); return; }
    setDupChecking(true);
    // Include current MCQ options so same question with different options isn't flagged
    const savedOpts = JSON.parse(localStorage.getItem("tempOptions") || "[]");
    const optionTexts = savedOpts.map(o => (o.option || "").trim()).filter(Boolean);
    try {
      const res = await fetch(`${API_URL}/api/questions/check-duplicate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          questionHtml: html,
          optionTexts,
          answerHtml:   formData.answer           || "",
          solutionHtml: formData.answerExplanation || "",
          topicId:      formData.topic             || undefined,
        }),
      });
      const data = await res.json();
      if (data.isDuplicate) {
        const hint = data.subject ? ` — already exists under "${data.subject}"` : "";
        setDupError(`This question already exists in the system${hint}.`);
      } else {
        setDupError("");
      }
    } catch {
      setDupError("");
    } finally {
      setDupChecking(false);
    }
  };

  const handleSubjectChange = async (subjectId) => {
    setFormData((p) => ({ ...p, subject: subjectId, module: "", topic: "", section: "" }));
    setModules([]);
    setTopics([]);
    setSections([]);
    clearError("subject");
    if (subjectId) {
      setLoadingModules(true);
      try {
        const mods = await fetchModulesBySubject(subjectId);
        setModules(mods);
      } catch {
        setModules([]);
      } finally {
        setLoadingModules(false);
      }
    }
  };

  const handleModuleChange = async (moduleId) => {
    setFormData((p) => ({ ...p, module: moduleId, topic: "", section: "" }));
    setTopics([]);
    setSections([]);
    clearError("module");
    if (moduleId) {
      setLoadingTopics(true);
      try {
        const tops = await fetchTopicsByModule(moduleId);
        setTopics(tops);
      } catch {
        setTopics([]);
      } finally {
        setLoadingTopics(false);
      }
    }
  };

  const handleTopicChange = async (topicId) => {
    setFormData((p) => ({ ...p, topic: topicId, section: "" }));
    setSections([]);
    clearError("topic");
    if (topicId) {
      setLoadingSections(true);
      try {
        const secs = await fetchSectionsByTopic(topicId);
        setSections(secs);
      } catch {
        setSections([]);
      } finally {
        setLoadingSections(false);
      }
    }
  };

  const handleSectionChange = (value) => {
    setFormData((p) => ({ ...p, section: value }));
    clearError("section");
  };

  const validate = () => {
    const e = {};
    if (!formData.subject)              e.subject        = "Subject is required";
    if (!formData.difficultyLevel)      e.difficultyLevel = "Difficulty level is required";
    if (!formData.questionType)         e.questionType   = "Question type is required";
    if (isHtmlEmpty(formData.question) && !formData.questionAttachment)
      e.question = "Question text or image is required";
    if (dupError) e.question = dupError;
    if (isMCQ) {
      const tempOpts = JSON.parse(localStorage.getItem("tempOptions") || "[]");
      if (!tempOpts.some((o) => o.isCorrect)) e.answer = "Please mark at least one option as correct";
    } else {
      if (isHtmlEmpty(formData.answer) && !formData.answerAttachment)
        e.answer = "Answer text or image is required";
    }
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const ve = validate();
    if (Object.keys(ve).length > 0) {
      setErrors(ve);
      setErrorMessage("Please correct the errors below.");
      return;
    }
    const tempOptions = JSON.parse(localStorage.getItem("tempOptions") || "[]");
    if (isMCQ && tempOptions.length === 0) {
      setErrorMessage("Please add at least one option before submitting.");
      return;
    }
    setLoading(true);
    try {
      // For MCQ, answer is resolved after options are saved; send placeholder so schema passes
      const payload = isMCQ ? { ...formData, answer: "TBD" } : { ...formData };
      payload.labels = formData.labels.map((l) => l.value);
      payload.alternateAnswers = formData.alternateAnswers.map((a) => a.trim()).filter(Boolean);
      // Only a code question has test cases; switching type away drops them.
      payload.testCases = formData.questionType === CODE_QUESTION_TYPE ? cleanTestCases(formData.testCases) : [];
      payload.starterCode = formData.questionType === CODE_QUESTION_TYPE ? formData.starterCode : "";
      const created = await createQuestion(payload);
      const questionId = created._id;

      if (tempOptions.length > 0) {
        const optionsPayload = tempOptions.map((o) => ({
          option: o.option,
          optionAttachment: o.optionAttachment || "",
          questionId,
          createdDateTime: new Date().toISOString(),
          updatedDateTime: new Date().toISOString(),
        }));
        const res = await fetch(`${API_URL}/api/multipleoptions`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ options: optionsPayload }),
        });
        if (!res.ok) throw new Error("Failed to save options");
        const saved = await res.json();

        if (isMCQ) {
          // Map isCorrect flag by index (insertMany preserves order)
          const savedOptions = saved.options || [];
          const correctIds = savedOptions
            .filter((_, i) => tempOptions[i]?.isCorrect)
            .map((o) => o._id.toString());
          const answer = correctIds.join(",");
          await fetch(`${API_URL}/api/questions/${questionId}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ answer }),
          });
        }

        localStorage.removeItem("tempOptions");
      }
      navigate("/questions", { state: { successMessage: "Question created successfully!" } });
    } catch (err) {
      setErrorMessage(err.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="af-page qf-page">
      {loading && (
        <div className="af-overlay">
          <div className="af-spinner" />
          <p className="af-overlay-text">Saving question…</p>
        </div>
      )}

      <div className="af-card qf-card">
        <div className="qf-head">
          <div className="qf-head-text">
            <h2 className="qf-title">New question</h2>
            <p className="qf-subtitle">Write the question and its answer, then say where it belongs.</p>
          </div>
        </div>

        {errorMessage && (
          <div className="af-alert af-alert-error" style={{ margin: "16px 32px 0" }}>
            {errorMessage}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="af-body qf-layout">
            <div className="qf-main">
            <div className="af-section">
              <p className="af-section-title">Question</p>
              <Field label="Question Text" required error={errors.question}>
                <div style={errors.question || dupError ? errorBorder : {}}>
                  <RichTextEditor
                    value={formData.question}
                    onChange={(html) => {
                      setFormData(p => ({ ...p, question: html }));
                      clearError("question");
                      setDupError("");
                    }}
                    onBlur={(html) => checkDuplicate(html)}
                    placeholder="Type the full question here… (paste images directly)"
                    toolbar="full"
                    height={180}
                  />
                </div>
                {dupChecking && (
                  <span style={{ fontSize: 12, color: "#6b7280", marginTop: 4, display: "block" }}>
                    Checking for duplicates…
                  </span>
                )}
                {dupError && !errors.question && (
                  <span className="af-error">{dupError}</span>
                )}
              </Field>
              <Field label="Question Image (optional)" error={errors.questionAttachment}>
                <ImageUploadField
                  value={formData.questionAttachment}
                  onChange={(url) => setFormData((p) => ({ ...p, questionAttachment: url }))}
                />
              </Field>

              {isMCQ && (
                <div className="af-options-block">
                  <div className="af-action-strip">
                    <button
                      type="button"
                      className="af-secondary-btn"
                      onClick={() => setShowOptionDialog(true)}
                    >
                      + Add Options
                    </button>
                  </div>
                  <OptionsTable
                    questionId={formData._id}
                    openDialogExternally={showOptionDialog}
                    setOpenDialogExternally={setShowOptionDialog}
                    onOptionsChange={() => clearError("answer")}
                  />
                  {errors.answer && <span className="af-error">{errors.answer}</span>}
                </div>
              )}
            </div>

            <div className="af-section">
              <p className="af-section-title">Answer</p>
              {!isMCQ && (
                <Field label="Answer" required error={errors.answer}>
                  <div style={errors.answer ? errorBorder : {}}>
                    <RichTextEditor
                      value={formData.answer}
                      onChange={(html) => { setFormData(p => ({ ...p, answer: html })); clearError("answer"); }}
                      placeholder="Correct answer… (paste images directly)"
                      toolbar="full"
                      height={150}
                    />
                  </div>
                </Field>
              )}
              {!isMCQ && (
                <Field label="Alternate Answers (optional)">
                  <AlternateAnswersField
                    value={formData.alternateAnswers}
                    onChange={(alternateAnswers) => setFormData((p) => ({ ...p, alternateAnswers }))}
                  />
                </Field>
              )}
              {formData.questionType === CODE_QUESTION_TYPE && (
                <Field label="Test cases (optional)">
                  <TestCasesField
                    value={formData.testCases}
                    onChange={(testCases) => setFormData((p) => ({ ...p, testCases }))}
                  />
                </Field>
              )}
              {formData.questionType === CODE_QUESTION_TYPE && (
                <Field label="Starter code (optional)">
                  <StarterCodeField
                    value={formData.starterCode}
                    onChange={(starterCode) => setFormData((p) => ({ ...p, starterCode }))}
                  />
                </Field>
              )}
              {isMCQ && (
                <p className="af-mcq-answer-hint">
                  ✓ Mark correct option(s) using the checkboxes in the Options section above.
                </p>
              )}
              <Field label="Answer Image (optional)" error={errors.answerAttachment}>
                <ImageUploadField
                  value={formData.answerAttachment}
                  onChange={(url) => setFormData((p) => ({ ...p, answerAttachment: url }))}
                />
              </Field>
              <Field label="Explanation" error={errors.answerExplanation}>
                <RichTextEditor
                  value={formData.answerExplanation}
                  onChange={(html) => setFormData(p => ({ ...p, answerExplanation: html }))}
                  placeholder="Why is this the correct answer… (paste images directly)"
                  toolbar="full"
                  height={200}
                />
              </Field>
            </div>
            </div>
            <aside className="qf-side">
            <div className="af-section">
              <p className="af-section-title">Classification</p>
              <div className="af-row">
                <Field label="Subject" required error={errors.subject}>
                  <HierarchyDropdown
                    options={subjects}
                    value={formData.subject}
                    onChange={handleSubjectChange}
                    placeholder="Select subject…"
                    hasError={!!errors.subject}
                  />
                </Field>
                <Field label="Module" error={errors.module}>
                  <HierarchyDropdown
                    options={modules}
                    value={formData.module}
                    onChange={handleModuleChange}
                    placeholder={formData.subject ? "Select module…" : "Select a subject first"}
                    isDisabled={!formData.subject}
                    isLoading={loadingModules}
                    hasError={!!errors.module}
                  />
                </Field>
              </div>
              <div className="af-row">
                <Field label="Topic" error={errors.topic}>
                  <HierarchyDropdown
                    options={topics}
                    value={formData.topic}
                    onChange={handleTopicChange}
                    placeholder={formData.module ? "Select topic…" : "Select a module first"}
                    isDisabled={!formData.module}
                    isLoading={loadingTopics}
                    hasError={!!errors.topic}
                  />
                </Field>
                <Field label="Section" error={errors.section}>
                  <HierarchyDropdown
                    options={sections}
                    value={formData.section}
                    onChange={handleSectionChange}
                    placeholder={formData.topic ? "Select or type a section…" : "Select a topic first"}
                    isDisabled={!formData.topic}
                    isLoading={loadingSections}
                    hasError={!!errors.section}
                    isCreatable
                  />
                </Field>
              </div>
              <div className="af-row">
                <Field label="Labels">
                  <MultiSelectDropdown
                    options={availableLabels}
                    value={formData.labels}
                    onChange={(val) => setFormData((p) => ({ ...p, labels: val }))}
                    placeholder="Add labels…"
                    isCreatable
                    onCreateOption={handleCreateLabel}
                  />
                </Field>
              </div>
              <div className="af-row">
                <Field label="Difficulty Level" required error={errors.difficultyLevel}>
                  <select
                    name="difficultyLevel"
                    value={formData.difficultyLevel}
                    onChange={handleChange}
                    className={errors.difficultyLevel ? "error-input" : ""}
                  >
                    <option value="">Select difficulty…</option>
                    {DIFFICULTY.map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                </Field>
                <Field label="Question Type" required error={errors.questionType}>
                  <select
                    name="questionType"
                    value={formData.questionType}
                    onChange={handleChange}
                    className={errors.questionType ? "error-input" : ""}
                  >
                    <option value="">Select type…</option>
                    {QUESTION_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </Field>
              </div>
            </div>

            <div className="af-section">
              <p className="af-section-title">Additional Details</p>
              <div className="af-row">
                <Field label="Notes" error={errors.notes}>
                  <input
                    type="text"
                    name="notes"
                    placeholder="Any extra notes…"
                    value={formData.notes}
                    onChange={handleChange}
                  />
                </Field>
                <Field label="Reference Link" error={errors.questionLink}>
                  <input
                    type="text"
                    name="questionLink"
                    placeholder="External resource URL"
                    value={formData.questionLink}
                    onChange={handleChange}
                  />
                </Field>
              </div>
              <div className="af-row">
                <Field label="Positive Marks" error={errors.markPositive}>
                  <input
                    type="number"
                    name="markPositive"
                    placeholder="e.g. 4"
                    value={formData.markPositive}
                    onChange={handleChange}
                  />
                </Field>
                <Field label="Negative Marks" error={errors.markNegative}>
                  <input
                    type="number"
                    name="markNegative"
                    placeholder="e.g. -1"
                    value={formData.markNegative}
                    onChange={handleChange}
                  />
                </Field>
              </div>
            </div>
            </aside>
          </div>

          <div className="qf-footer">
            <span className="qf-footer-note">Fields marked * are required</span>
            <div className="qf-footer-actions">
              <button type="button" className="qf-btn qf-btn-ghost" onClick={() => navigate("/questions")}>Cancel</button>
              <button type="submit" className="qf-btn qf-btn-primary" disabled={loading}>
                {loading ? "Saving…" : "Create question"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddQuestions;
